import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Change and cancel printable prescriptions (EXECDEV-176, migration 20261015001533), per
 * medication of a signed prescription. A signed prescription is never edited:
 *
 * - A change signs a new prescription with operation "change"; each medication on it names the
 *   signed medication it replaces (changeOf), which then reads as changed. Inside a visit it signs
 *   on that encounter; outside one the server files it the way a refill outside a visit is filed,
 *   on a closed "Prescription change" encounter with a one-line note.
 * - A cancel records a reason for one or more medications and may stop each on the chart's
 *   medication list. Inside a visit it names the visit; outside one it writes no note.
 */
/** A prescription lists at most ten medications, so an item index is 0–9. */
const ItemIndexSchema = z.number().int().min(0).max(9);
/** What a changed medication names: the signed prescription item it replaces. */
export const PrescriptionChangeLinkSchema = z
    .object({ printablePrescriptionId: IdSchema, itemIndex: ItemIndexSchema })
    .strict();
/** Why a physician cancels a medication. A change cancels the old medication as "changed". */
export const PrescriptionCancelReasonSchema = z.enum([
    "entered_in_error",
    "adverse_reaction",
    "no_longer_needed",
    "patient_declined",
    "other",
]);
const CancellationReasonCodeSchema = z.union([
    PrescriptionCancelReasonSchema,
    z.literal("changed"),
]);
/** Who did something to a prescription: the workforce member, by name when the clinic has one. */
const PrescriptionActorSchema = z.object({
    userId: IdSchema,
    displayName: z.string().optional(),
});
/** A signed medication as it is referred to from another prescription. */
const PrescriptionItemReferenceSchema = z.object({
    printablePrescriptionId: IdSchema,
    itemIndex: ItemIndexSchema,
    encounterId: IdSchema.optional(),
    signedAt: IsoDateTimeSchema.optional(),
});
/**
 * What became of one medication of a signed prescription since it was signed, as the visit's
 * prescription list reports it (itemStates, one entry per medication in order).
 */
export const PrintablePrescriptionItemStateSchema = z.object({
    itemIndex: ItemIndexSchema,
    medicationDisplay: z.string(),
    status: z.enum(["active", "cancelled", "changed"]),
    /** Present once cancelled or changed. */
    cancellation: z
        .object({
        reasonCode: CancellationReasonCodeSchema,
        reasonText: z.string().optional(),
        cancelledAt: IsoDateTimeSchema,
        cancelledBy: PrescriptionActorSchema,
        /** The visit the cancellation was made in; absent outside a visit. */
        encounterId: IdSchema.optional(),
        /** The cancellation also stopped the medication on the chart. */
        medicationStopped: z.boolean(),
        /** A change: the medication that replaced this one. */
        replacedBy: PrescriptionItemReferenceSchema.optional(),
    })
        .optional(),
    /**
     * A medication of a change: the signed medication it replaced, and whether it was listed again
     * exactly as signed (re-issued with others) rather than changed.
     */
    replaces: PrescriptionItemReferenceSchema.extend({
        medicationDisplay: z.string(),
        reissued: z.boolean(),
    }).optional(),
    /**
     * An active medication on the chart: whether this prescription started it (stopping is the
     * default when cancelling), and whether cancelling can stop it (its chart entry is current and
     * active, and no later prescription renewed or changed it).
     */
    chart: z.object({ startedHere: z.boolean(), stoppable: z.boolean() }).optional(),
});
export const PrintablePrescriptionItemStatesSchema = z.array(PrintablePrescriptionItemStateSchema);
/** What a cancellation names, as the database receives it (the site travels beside it). */
const cancellationShape = {
    idempotencyKey: z.string().min(8).max(128),
    patientId: IdSchema,
    printablePrescriptionId: IdSchema,
    items: z
        .array(z.object({ itemIndex: ItemIndexSchema, stopMedication: z.boolean().optional() }).strict())
        .min(1)
        .max(10)
        .refine((items) => new Set(items.map((item) => item.itemIndex)).size === items.length, "Each medication is cancelled once"),
    reasonCode: PrescriptionCancelReasonSchema,
    reasonText: z
        .string()
        .max(500)
        .regex(/^[^\p{Cc}]*$/u, "A reason is one line")
        .optional(),
    /** The visit the cancellation is made in; absent outside a visit. */
    encounterId: IdSchema.optional(),
};
/** "Other" says why. */
const otherReasonIsSpelledOut = (request) => request.reasonCode !== "other" || (request.reasonText?.trim().length ?? 0) >= 3;
const otherReasonIssue = { path: ["reasonText"], message: "Say why the prescription is cancelled" };
/** The cancellation the database receives (api.cancel_printable_prescription p_input). */
export const PrintablePrescriptionCancelInputSchema = z
    .object(cancellationShape)
    .strict()
    .refine(otherReasonIsSpelledOut, otherReasonIssue);
/** POST /v1/patients/:patientId/prescription-cancellations. */
export const CancelPrintablePrescriptionRequestSchema = z
    .object({ siteId: IdSchema, ...cancellationShape })
    .strict()
    .refine(otherReasonIsSpelledOut, otherReasonIssue);
/**
 * A stopped medication's chart entry: the next version of the entry the medication was signed
 * into, with status inactive.
 */
const StoppedChartMedicationSchema = z.object({
    medicationId: IdSchema,
    version: z.number().int().min(2),
    status: z.literal("inactive"),
    /** The entry as the chart lists it: the name and directions it kept. */
    display: z.string(),
    sig: z.string().optional(),
    superseded: z.literal(true),
    supersedes: z.object({ medicationId: IdSchema, version: z.number().int().positive() }),
});
/** What a cancellation recorded; a retry with the same key answers the same, replayed. */
export const PrintablePrescriptionCancellationSchema = z.object({
    printablePrescriptionId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema.optional(),
    reasonCode: PrescriptionCancelReasonSchema,
    reasonText: z.string().optional(),
    cancelledAt: IsoDateTimeSchema,
    cancelledBy: PrescriptionActorSchema,
    replayed: z.boolean(),
    items: z
        .array(z.object({
        index: ItemIndexSchema,
        medicationDisplay: z.string(),
        stopped: z.boolean(),
        chartMedication: StoppedChartMedicationSchema.optional(),
    }))
        .min(1),
});
/**
 * The "Prescription change" encounter a change outside a visit opened, signed and closed.
 * `created` is false when a retried idempotency key answered with the change it already signed.
 */
export const PrescriptionChangeVisitSchema = z
    .object({ encounterId: IdSchema, noteId: IdSchema, created: z.boolean() })
    .strict();
//# sourceMappingURL=prescription-changes.js.map